/**
 * Error toast UI wrapper for Shopify/cart validations.
 * Listens to existing cart error containers ([data-cart-errors]) and mirrors messages into a toast.
 */
(() => {
  const SELECTORS = {
    cartErrors: '[data-cart-errors]',
    cartErrorMessage: '[data-error-message]',
    cartErrorClose: '[data-cart-error-close]',
    termsErrorMessage: '[data-terms-error-message]',
    formErrorsContainer: '[data-cart-errors-container]',
    formError: '[data-error]',
    formErrorClose: '[data-close-error]',
    modal: '[data-error-modal]',
    modalOverlay: '[data-error-modal-overlay]',
    modalMessage: '[data-error-modal-message]',
    modalClose: '[data-error-modal-close]',
    modalActions: '[data-error-modal-actions]',
    modalCta: '[data-error-modal-cta]',
    modalOpenCart: '[data-error-modal-open-cart]',
    // Prefer the same toggle your header uses (anchor with data-cart-drawer-toggle).
    cartDrawerToggle: '.cart__toggle[data-cart-drawer-toggle], a[data-cart-drawer-toggle], [data-cart-drawer-toggle], [href="#cart-drawer"]',
  };

  const CLASSES = {
    expanded: 'is-expanded',
    visible: 'is-visible',
    open: 'is-open',
    cartDrawerOpenBody: 'js-drawer-open-cart',
  };

  let lastMessage = '';
  let lastShownAt = 0;
  let observedHolders = new WeakSet();
  let lastActiveElement = null;
  let isOpen = false;
  let didLockScroll = false;

  function lockScroll() {
    // If the cart drawer is open, the theme already locks scroll. Avoid stacking scroll-lock strategies.
    if (document.body.classList.contains(CLASSES.cartDrawerOpenBody)) return;
    if (document.documentElement.hasAttribute('data-scroll-locked')) return;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.dataset.ggErrorModalScrollY = String(scrollY);
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    didLockScroll = true;
  }

  function unlockScroll() {
    if (!didLockScroll) return;

    const scrollY = parseInt(document.body.dataset.ggErrorModalScrollY || '0', 10) || 0;
    delete document.body.dataset.ggErrorModalScrollY;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
    didLockScroll = false;
  }

  function getModalEls() {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return null;
    return {
      modal,
      overlay: modal.querySelector(SELECTORS.modalOverlay),
      message: modal.querySelector(SELECTORS.modalMessage),
      close: modal.querySelector(SELECTORS.modalClose),
      actions: modal.querySelector(SELECTORS.modalActions),
      cta: modal.querySelector(SELECTORS.modalCta),
    };
  }

  function closeModal({ restoreFocus = true } = {}) {
    const els = getModalEls();
    if (!els) return;

    els.modal.classList.remove(CLASSES.open);
    els.modal.setAttribute('aria-hidden', 'true');
    els.modal.hidden = true;
    isOpen = false;
    unlockScroll();

    if (restoreFocus && lastActiveElement && typeof lastActiveElement.focus === 'function') {
      lastActiveElement.focus();
    }
    lastActiveElement = null;
    // Allow showing the same validation again after the user closes the modal.
    lastMessage = '';
    lastShownAt = 0;

    if (els.actions) els.actions.hidden = true;
  }

  function updateActions(message) {
    const els = getModalEls();
    if (!els?.actions) return;
    // Always show actions (Sign in + Check your cart) for purchase validations.
    els.actions.hidden = false;
  }

  function openModal(message) {
    const els = getModalEls();
    if (!els || !els.message) return;

    els.message.textContent = message;
    updateActions(message);
    els.modal.hidden = false;
    els.modal.setAttribute('aria-hidden', 'false');
    els.modal.classList.add(CLASSES.open);
    isOpen = true;
    lockScroll();

    // Focus close button so SR reads alertdialog content reliably.
    els.close?.focus?.();
  }

  function readText(el) {
    if (!el) return '';
    return (el.textContent || '').trim();
  }

  function getCartErrorMessage(holder) {
    // Prefer the standard cart error message slot.
    const msg = readText(holder.querySelector(SELECTORS.cartErrorMessage));
    if (msg) return msg;

    return '';
  }

  function maybeToastFromHolder(holder) {
    if (!holder.classList.contains(CLASSES.expanded)) return;

    const msg = getCartErrorMessage(holder);
    if (!msg) return;

    // Avoid spamming duplicates during rapid cart re-renders, but allow re-triggering later.
    const now = Date.now();
    if (msg === lastMessage && now - lastShownAt < 600) return;
    lastMessage = msg;
    lastShownAt = now;
    if (!isOpen) lastActiveElement = document.activeElement;
    openModal(msg);
  }

  function getFormErrorMessage(container) {
    const errEl = container.querySelector(SELECTORS.formError);
    const msg = readText(errEl);
    return msg;
  }

  function maybeToastFromFormErrorContainer(container) {
    // Most flows add `.is-visible` on the container, but we fall back to presence of the error node.
    const hasVisibleClass = container.classList.contains(CLASSES.visible);
    const hasErrorNode = !!container.querySelector(SELECTORS.formError);
    if (!hasVisibleClass && !hasErrorNode) return;

    const msg = getFormErrorMessage(container);
    if (!msg) return;

    const now = Date.now();
    if (msg === lastMessage && now - lastShownAt < 600) return;
    lastMessage = msg;
    lastShownAt = now;
    if (!isOpen) lastActiveElement = document.activeElement;
    openModal(msg);
  }

  function observeHolder(holder) {
    if (!holder || observedHolders.has(holder)) return;
    observedHolders.add(holder);

    // Close toast when the native cart error is dismissed.
    holder.addEventListener(
      'click',
      (e) => {
        const target = e.target;
        const close = target?.matches?.(SELECTORS.cartErrorClose) ? target : target?.closest?.(SELECTORS.cartErrorClose);
        if (close) closeModal({ restoreFocus: false });
      },
      { passive: true }
    );

    const observer = new MutationObserver(() => {
      maybeToastFromHolder(holder);
    });

    observer.observe(holder, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
      childList: true,
      characterData: true,
    });

    // Handle initial state if the error is already expanded.
    maybeToastFromHolder(holder);
  }

  function observeFormErrorContainer(container) {
    if (!container || observedHolders.has(container)) return;
    observedHolders.add(container);

    container.addEventListener(
      'click',
      (e) => {
        const target = e.target;
        const close = target?.matches?.(SELECTORS.formErrorClose) ? target : target?.closest?.(SELECTORS.formErrorClose);
        if (close) closeModal({ restoreFocus: false });
      },
      { passive: true }
    );

    const observer = new MutationObserver(() => {
      maybeToastFromFormErrorContainer(container);
    });

    observer.observe(container, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
      childList: true,
      characterData: true,
    });

    maybeToastFromFormErrorContainer(container);
  }

  function maybeToastFromTermsErrorElement(el) {
    const msg = readText(el);
    if (!msg) return;

    const now = Date.now();
    if (msg === lastMessage && now - lastShownAt < 600) return;
    lastMessage = msg;
    lastShownAt = now;

    if (!isOpen) lastActiveElement = document.activeElement;
    openModal(msg);
  }

  function observeTermsErrorElement(el) {
    if (!el || observedHolders.has(el)) return;
    observedHolders.add(el);

    const observer = new MutationObserver(() => {
      maybeToastFromTermsErrorElement(el);
    });

    observer.observe(el, {
      subtree: true,
      childList: true,
      characterData: true,
    });

    maybeToastFromTermsErrorElement(el);
  }

  function observeForNewHolders() {
    const root = document.documentElement;
    const docObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes || []) {
          if (!(node instanceof HTMLElement)) continue;

          if (node.matches?.(SELECTORS.cartErrors)) {
            observeHolder(node);
          }
          if (node.matches?.(SELECTORS.formErrorsContainer)) {
            observeFormErrorContainer(node);
          }
          if (node.matches?.(SELECTORS.termsErrorMessage)) {
            observeTermsErrorElement(node);
          }

          node.querySelectorAll?.(SELECTORS.cartErrors).forEach(observeHolder);
          node.querySelectorAll?.(SELECTORS.formErrorsContainer).forEach(observeFormErrorContainer);
          node.querySelectorAll?.(SELECTORS.termsErrorMessage).forEach(observeTermsErrorElement);
        }
      }
    });

    docObserver.observe(root, { childList: true, subtree: true });
  }

  function initToastClose() {
    const els = getModalEls();
    if (!els) return;

    els.close?.addEventListener?.('click', (e) => {
      e.preventDefault();
      closeModal();
    });

    els.overlay?.addEventListener?.('click', (e) => {
      e.preventDefault();
      closeModal();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        closeModal();
      }
    });

    const openCartLink = els.modal.querySelector?.(SELECTORS.modalOpenCart);
    openCartLink?.addEventListener?.('click', (e) => {
      // If the cart is already open, don't toggle it (can cause odd edge-case states). Just close this modal.
      if (document.body.classList.contains(CLASSES.cartDrawerOpenBody)) {
        e.preventDefault();
        closeModal({ restoreFocus: false });
        return;
      }

      const toggles = Array.from(document.querySelectorAll(SELECTORS.cartDrawerToggle));
      // Prefer a toggle that's outside the drawer itself (usually the header cart icon).
      const preferredToggle =
        toggles.find((t) => !t.closest(SELECTORS.cartDrawer)) ||
        toggles[0];

      if (!preferredToggle) return;

      e.preventDefault();
      preferredToggle.click();
      closeModal({ restoreFocus: false });
    });
  }

  function init() {
    initToastClose();
    document.querySelectorAll(SELECTORS.cartErrors).forEach(observeHolder);
    document.querySelectorAll(SELECTORS.formErrorsContainer).forEach(observeFormErrorContainer);
    document.querySelectorAll(SELECTORS.termsErrorMessage).forEach(observeTermsErrorElement);
    observeForNewHolders();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


